import React, { ChangeEvent } from 'react';
import { IAuthState, IRootState } from '../../../../@types/redux';
import { connect } from 'react-redux';
import firebase from 'firebase/app';
import { createSelector } from 'reselect';
import Typography from '@material-ui/core/Typography';
import Toolbar from '@material-ui/core/Toolbar';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Icon from '@material-ui/core/Icon';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import LinearProgress from '@material-ui/core/LinearProgress';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import GeneralFunctions from '../../../../store/general/functions';
import PumpingStationMotorSizeHelper, { IPumpingStationMotorSize } from '../../../../@types/model/masterData/pumpingStation/motorSize';
import MasterDataPumpingStationMotorSizeFunctions from '../../../../store/masterData/pumpingStation/motorSize/functions';

interface IPumpingStationMotorSizeListProps {
    auth : IAuthState;

    isLoading : boolean;
    motorSizes : Array<IPumpingStationMotorSize>;
}

interface IPumpingStationMotorSizeListState {
    searchText : string;

    selected : IPumpingStationMotorSize | null;

}

class PumpingStationMotorSizeListComponent extends React.PureComponent<IPumpingStationMotorSizeListProps, IPumpingStationMotorSizeListState> {
    constructor(props : IPumpingStationMotorSizeListProps) {
        super(props);
        this.state = {
            searchText: '',
            selected: null,
        };
    }

    public readonly componentDidMount = () => {
        this.load();
    };

    private readonly load = () => {
        MasterDataPumpingStationMotorSizeFunctions.load();
    };

    private readonly onTypeClick = (event : React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        this.setState({
            selected: this.props.motorSizes.find(n => n.id === event.currentTarget.id) ?? null,
        });
    };

    private readonly onSearchChanged = (event : ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        if (isNaN(Number(event.currentTarget.value))) return;
        this.setState({
            searchText: event.currentTarget.value,
        });
    };

    private readonly onSizeChange = (event : ChangeEvent<HTMLInputElement>) => {
        const { selected } = this.state;

        if (!selected) return;
        if (isNaN(Number(event.currentTarget.value))) return;

        this.setState({
            selected: {
                ...selected,
                size: Number(event.currentTarget.value),
            },
            
        });
    };

    private readonly onClearClick = () => {
        this.setState({
            searchText: '',
        });
    };

    private readonly onAddClick = () => {
        if (!this.state.searchText) return;
        if (isNaN(Number(this.state.searchText))) return;

        const size = Number(this.state.searchText);

        if (this.props.motorSizes.some(n => n.size === size)) {
            GeneralFunctions.generalShowErrorSnackbar(`${size} already added.`);
            return;
        }

        const pump : IPumpingStationMotorSize = {
            id: '',
            size: size,
            createdBy: this.props.auth.session?.firebaseUser.uid ?? '',
            createdByEmployee: this.props.auth.session?.employee.EmployeeNumber ?? '',
            createdByName: this.props.auth.session?.employee.Name ?? '',
            createdOn: firebase.firestore.Timestamp.now().toMillis(),
            updatedBy: this.props.auth.session?.firebaseUser.uid ?? '',
            updatedByEmployee: this.props.auth.session?.employee.EmployeeNumber ?? '',
            updatedByName: this.props.auth.session?.employee.Name ?? '',
            updatedOn: firebase.firestore.Timestamp.now().toMillis(),
            isActive: true,
        };

        PumpingStationMotorSizeHelper.save(pump);

        this.setState({
            searchText: '',            
        });
    };

    private readonly onSaveClick = async (event : React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();

        const { selected } = this.state;

        if (!selected) return;

        if (this.props.motorSizes.some(n => n.id !== selected.id && n.size === selected.size)) {
            GeneralFunctions.generalShowErrorSnackbar(`${this.state.searchText} already added.`);
            return;
        }

        PumpingStationMotorSizeHelper.save({
            ...selected,
        });
    };

    private readonly onDeleteClick = (event : React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();

        const selected = this.props.motorSizes.find(x => x.id === event.currentTarget.value);
        if (!selected) return;

        PumpingStationMotorSizeHelper.save({
            ...selected,
            isActive: !selected.isActive,
        });
    };

    private readonly getMotorSizes = (state : IPumpingStationMotorSizeListState, props : IPumpingStationMotorSizeListProps) => props.motorSizes;
    private readonly getSearchText = (state : IPumpingStationMotorSizeListState) => state.searchText;

    private readonly getFilteredSizes = createSelector(
        [this.getMotorSizes, this.getSearchText],
        (motorSizes, searchText) => {
            return motorSizes
                .filter(n => n.size.toString().toLocaleLowerCase().includes(searchText.toLocaleLowerCase()))
                .sort((a, b) => a.size - b.size);
        },
    );

    private readonly getTotal = createSelector(
        [this.getMotorSizes],
        (motorSizes) => {
            return motorSizes.length;
        },
    );

    public readonly render = () => {
        const { searchText, selected } = this.state;
        const { isLoading } = this.props;

        const motorSizes = this.getFilteredSizes(this.state, this.props);
        const total = this.getTotal(this.state, this.props);

        return (
            <div className={'fdc ais flx1 p10'}>
                <div className='fdr flx1 hfill'>
                    <div className='fdc flx1 hfill'>
                        <Paper className='fdc flx1 hfill m10'>
                            <Toolbar className={'fdr aic'}>
                                <TextField
                                    value={searchText}
                                    onChange={this.onSearchChanged}
                                    placeholder='Search'
                                    margin='dense'
                                    fullWidth
                                    type={'number'}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <Icon>search</Icon>
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <div>
                                                {
                                                    searchText &&
                                                    <Tooltip title='Clear'>
                                                        <IconButton size='small' onClick={this.onClearClick}>
                                                            <Icon className='cr'>close</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                }
                                            </div>
                                        ),
                                    }}
                                />
                                <Tooltip title='Add'>
                                    <div>
                                        <IconButton color='primary' onClick={this.onAddClick} aria-label='Add'>
                                            <Icon>add</Icon>
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            </Toolbar>
                            <Divider />
                            <Typography variant={'subtitle2'} className={'fdr pl20 pr30 pb5 pt5'}>
                                Total {total}
                            </Typography>
                            <Divider />
                            <List className={'fdc flx1 oya'}>
                                <div className='mnh4'>
                                    { isLoading && <LinearProgress />}
                                </div>
                                {
                                    motorSizes.map(n => (
                                        <ListItem button key={n.id} id={n.id} onClick={this.onTypeClick} selected={selected?.id === n.id} >
                                            <ListItemText
                                                className={n.isActive ? '' : 'cr'}
                                                primary={`${n.size} kW`}
                                            />
                                            {
                                                (n.isActive) &&
                                                <ListItemSecondaryAction>
                                                    <Tooltip title='Disable'>
                                                        <IconButton className='cr' value={n.id} onClick={this.onDeleteClick} aria-label='Delete'>
                                                            <Icon>delete</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                </ListItemSecondaryAction>
                                            }
                                            {
                                                (!n.isActive) &&
                                                <ListItemSecondaryAction>
                                                    <Tooltip title='Enable'>
                                                        <IconButton color='primary' value={n.id} onClick={this.onDeleteClick} aria-label='Delete'>
                                                            <Icon>check</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                </ListItemSecondaryAction>
                                            }
                                        </ListItem>
                                    ))
                                }
                            </List>
                        </Paper>
                    </div>
                    <div className='fdc flx4 hfill'>
                        {
                            !!selected &&
                            <Paper className='fdc flx1 hfill m10'>
                                <Toolbar className={'fdr aic'}>
                                    <Typography variant='h5' color='inherit'>
                                    Motor Size - {selected.size}
                                    </Typography>
                                    <span className='flx1' />
                                    <Tooltip title='Save'>
                                        <div>
                                            <IconButton color='primary' onClick={this.onSaveClick} aria-label='Save'>
                                                <Icon>save</Icon>
                                            </IconButton>
                                        </div>
                                    </Tooltip>
                                </Toolbar>
                                <Divider />
                                <div className='fdc flx1 hfill'>
                                    <div className='fdc flx1 oya'>
                                        <div className='aic p5 mb10 pr20'>
                                            <FormControl fullWidth>
                                                <TextField
                                                    autoComplete='off'
                                                    id='size'
                                                    label='Size'
                                                    value={selected.size ? selected.size : ''}
                                                    required
                                                    error={!selected.size}
                                                    onChange={this.onSizeChange}
                                                    margin='normal'
                                                    className={'TextField'}
                                                    type='number'
                                                />
                                                {
                                                    !selected.size &&
                                                    <FormHelperText error>Required</FormHelperText>
                                                }
                                            </FormControl>
                                        </div>
                                    </div>
                                </div>
                            </Paper>
                        }
                    </div>
                </div>
            </div>
        );
    };
}

const mapStateToProps = (state : IRootState) => {
    return {
        auth: state.auth,
        isLoading: state.masterData.pumpingStation.motorSize.isLoading,
        motorSizes: state.masterData.pumpingStation.motorSize.motorSizes,
    };
};

const PumpingStationMotorSizeList = connect(
    mapStateToProps,
)(PumpingStationMotorSizeListComponent);

export default PumpingStationMotorSizeList;
